import { logError } from "@/logging/logging";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { useCallback, useEffect, useMemo, useState } from "react";
import { flattenFueFlags, splitFueFlags, type FueFlags } from "./fue-flags";

/** The status of loading the FUE flags of the current user. */
type FetchStatus = "uninitialized" | "loading" | "loaded" | "failed";

type UseFueFlags = {
  /** Whether the flags of the current user have been loaded. */
  areFueFlagsLoaded: boolean;

  /** The flags of the current user, empty until they have been loaded. */
  fueFlags: FueFlags;

  /** Persistently changes the flags, ignored until the current ones have been loaded. */
  updateFueFlags(update: (current: FueFlags) => FueFlags): void;
};

/**
 * Loads and persists the FUE (first user experience) flags of the current user.
 *
 * The flags are stored centrally by the Core API, so that they are shared across devices and
 * with the other FARO applications.
 *
 * @returns The flags of the current user and a callback to change them
 */
export function useFueFlags(): UseFueFlags {
  const { coreApiClient } = useApiClientContext();
  const currentUser = useAppSelector(selectCurrentUser);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>("uninitialized");
  const [fueFlags, setFueFlags] = useState<FueFlags>({ known: {}, other: {} });

  useEffect(() => {
    if (!currentUser || fetchStatus !== "uninitialized") return;

    setFetchStatus("loading");
    coreApiClient
      .getUserFueFlags()
      .then((flags) => {
        setFueFlags(splitFueFlags(flags));
        setFetchStatus("loaded");
      })
      .catch((error) => {
        logError(error);
        setFetchStatus("failed");
      });
  }, [coreApiClient, currentUser, fetchStatus]);

  const updateFueFlags = useCallback(
    (update: (current: FueFlags) => FueFlags) => {
      // Writing before the flags are read would drop the flags of the other applications
      if (fetchStatus !== "loaded") return;

      // Updating from the setter, so that a change is always applied to the newest flags
      setFueFlags((current) => {
        const updated = update(current);

        // The backend replaces all flags of the user, so the flags of other apps have to be sent too
        coreApiClient.setUserFueFlags(flattenFueFlags(updated)).catch(logError);

        return updated;
      });
    },
    [coreApiClient, fetchStatus],
  );

  return useMemo(
    () => ({
      areFueFlagsLoaded: fetchStatus === "loaded",
      fueFlags,
      updateFueFlags,
    }),
    [fetchStatus, fueFlags, updateFueFlags],
  );
}
